/**
 * Geographic taxonomy for the "Publish to job boards" drill-down.
 *
 * Continent  →  Sub-region  →  Country  →  Job boards operating there.
 *
 * `BOARD_COUNTRIES` maps each job-board id (see constants/jobBoards.js) to the
 * ISO-3166 alpha-2 country codes it serves, or the string 'global' for boards
 * that operate everywhere (those surface under every country + the "Worldwide"
 * shortcut).
 */
import { JOB_BOARDS } from './jobBoards';

export const BOARD_COUNTRIES = {
  // Global reach
  indeed: 'global',
  glassdoor: 'global',
  linkedin: 'global',
  monster: 'global',
  // Country / region specific
  adzuna: ['DE', 'GB'],
  bundesagentur: ['DE'],
  stepstone: ['DE'],
  xing: ['DE', 'AT', 'CH'],
  reed: ['GB'],
  totaljobs: ['GB'],
  'cv-library': ['GB'],
  finn: ['NO'],
  jobindex: ['DK'],
  pracuj: ['PL'],
  'cv-online': ['EE', 'LV', 'LT'],
};

// Continents → sub-regions → countries.
export const CONTINENTS = [
  {
    id: 'europe',
    name: 'Europe',
    regions: [
      {
        id: 'western-europe',
        name: 'Western Europe',
        countries: [
          { code: 'DE', name: 'Germany' },
          { code: 'AT', name: 'Austria' },
          { code: 'CH', name: 'Switzerland' },
          { code: 'FR', name: 'France' },
          { code: 'NL', name: 'Netherlands' },
          { code: 'BE', name: 'Belgium' },
        ],
      },
      {
        id: 'northern-europe',
        name: 'Northern Europe',
        countries: [
          { code: 'GB', name: 'United Kingdom' },
          { code: 'IE', name: 'Ireland' },
          { code: 'NO', name: 'Norway' },
          { code: 'SE', name: 'Sweden' },
          { code: 'DK', name: 'Denmark' },
          { code: 'FI', name: 'Finland' },
          { code: 'EE', name: 'Estonia' },
          { code: 'LV', name: 'Latvia' },
          { code: 'LT', name: 'Lithuania' },
        ],
      },
      {
        id: 'southern-europe',
        name: 'Southern Europe',
        countries: [
          { code: 'ES', name: 'Spain' },
          { code: 'IT', name: 'Italy' },
          { code: 'PT', name: 'Portugal' },
          { code: 'GR', name: 'Greece' },
        ],
      },
      {
        id: 'eastern-europe',
        name: 'Eastern Europe',
        countries: [
          { code: 'PL', name: 'Poland' },
          { code: 'CZ', name: 'Czechia' },
          { code: 'RO', name: 'Romania' },
          { code: 'HU', name: 'Hungary' },
        ],
      },
    ],
  },
  {
    id: 'north-america',
    name: 'North America',
    regions: [
      {
        id: 'northern-america',
        name: 'Northern America',
        countries: [
          { code: 'US', name: 'United States' },
          { code: 'CA', name: 'Canada' },
        ],
      },
      {
        id: 'central-america',
        name: 'Central America & Caribbean',
        countries: [{ code: 'MX', name: 'Mexico' }],
      },
    ],
  },
  {
    id: 'south-america',
    name: 'South America',
    regions: [
      {
        id: 'south-america-region',
        name: 'South America',
        countries: [
          { code: 'BR', name: 'Brazil' },
          { code: 'AR', name: 'Argentina' },
          { code: 'CL', name: 'Chile' },
          { code: 'CO', name: 'Colombia' },
        ],
      },
    ],
  },
  {
    id: 'asia',
    name: 'Asia',
    regions: [
      {
        id: 'eastern-asia',
        name: 'Eastern Asia',
        countries: [
          { code: 'CN', name: 'China' },
          { code: 'JP', name: 'Japan' },
          { code: 'KR', name: 'South Korea' },
        ],
      },
      {
        id: 'southern-asia',
        name: 'Southern Asia',
        countries: [
          { code: 'IN', name: 'India' },
          { code: 'PK', name: 'Pakistan' },
        ],
      },
      {
        id: 'south-eastern-asia',
        name: 'South-Eastern Asia',
        countries: [
          { code: 'SG', name: 'Singapore' },
          { code: 'ID', name: 'Indonesia' },
          { code: 'PH', name: 'Philippines' },
        ],
      },
      {
        id: 'western-asia',
        name: 'Western Asia (Middle East)',
        countries: [
          { code: 'AE', name: 'United Arab Emirates' },
          { code: 'SA', name: 'Saudi Arabia' },
          { code: 'IL', name: 'Israel' },
        ],
      },
    ],
  },
  {
    id: 'africa',
    name: 'Africa',
    regions: [
      {
        id: 'northern-africa',
        name: 'Northern Africa',
        countries: [
          { code: 'EG', name: 'Egypt' },
          { code: 'MA', name: 'Morocco' },
        ],
      },
      {
        id: 'sub-saharan-africa',
        name: 'Sub-Saharan Africa',
        countries: [
          { code: 'ZA', name: 'South Africa' },
          { code: 'NG', name: 'Nigeria' },
          { code: 'KE', name: 'Kenya' },
        ],
      },
    ],
  },
  {
    id: 'oceania',
    name: 'Oceania',
    regions: [
      {
        id: 'australasia',
        name: 'Australia & New Zealand',
        countries: [
          { code: 'AU', name: 'Australia' },
          { code: 'NZ', name: 'New Zealand' },
        ],
      },
    ],
  },
];

// Boards that operate everywhere.
export const GLOBAL_BOARDS = JOB_BOARDS.filter((b) => BOARD_COUNTRIES[b.id] === 'global');

/** All boards that operate in a given ISO country code (global boards always included). */
export const boardsForCountry = (code) =>
  JOB_BOARDS.filter((b) => {
    const c = BOARD_COUNTRIES[b.id];
    return c === 'global' || (Array.isArray(c) && c.includes(code));
  });

/** Count of country-specific (non-global) boards available in a country. */
export const localBoardCount = (code) =>
  JOB_BOARDS.filter((b) => Array.isArray(BOARD_COUNTRIES[b.id]) && BOARD_COUNTRIES[b.id].includes(code)).length;

/** Count of country-specific boards across a whole region. */
export const regionLocalBoardCount = (region) =>
  region.countries.reduce((sum, c) => sum + localBoardCount(c.code), 0);

/** Count of country-specific boards across a whole continent. */
export const continentLocalBoardCount = (continent) =>
  continent.regions.reduce((sum, r) => sum + regionLocalBoardCount(r), 0);
